import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { isTooManyRequestsError, storableError } from '../../util/errors';
import { retryOn429 } from '../../util/sharetribeSafeQuery';
import { getSupportedProcessesInfo } from '../../transactions/transaction';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchStripeAccount } from '../../ducks/stripeConnectAccount.duck';
import {
  fetchCurrentUserNotifications,
  setCurrentUser,
  setCurrentUserHasListings,
  setCurrentUserOwnListing,
} from '../../ducks/user.duck';
import {
  pickOwnListeningProfile,
  summarizeOwnListeningProfile,
} from '../../util/ownListeningProfile';
import { queryOwnListeningProfiles } from '../../util/queryOwnListeningProfiles';
import {
  getStripeAccountData,
  stripeAccountReadyToPublish,
  syncOwnListingPayoutsReady,
} from '../../util/stripeConnect';
import { createImageVariantConfig, types as sdkTypes } from '../../util/sdkLoader';
import { IMAGE_FIELD_VARIANTS } from '../../util/avatarImage';
import {
  applyFavoriteBucketsToUser,
  favoriteEntityId,
  favoriteListingIdsFromUser,
  favoriteProfileIdsFromUser,
  partitionRecoveredFavorites,
  resolveFavoriteEntities,
} from '../../util/favorites';
import { attachInboxListenerFeedback } from '../../util/inboxListenerFeedback';
import { mergeInboxTransactions, UPCOMING_LAST_TRANSITIONS } from '../../util/inboxSort';
import { fetchFeaturedListingsThunk } from '../LandingPage/LandingPage.duck';

const { UUID } = sdkTypes;

const DASHBOARD_PAGE_SIZE = 50;

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const transactionQueryParams = only => {
  const processNames = getSupportedProcessesInfo().map(p => p.name);
  return {
    only,
    processNames,
    include: [
      'listing',
      'provider',
      'provider.profileImage',
      'customer',
      'customer.profileImage',
      'booking',
      'reviews',
    ],
    'fields.review': ['rating', 'type', 'createdAt', 'state'],
    'fields.transaction': [
      'createdAt',
      'processName',
      'processVersion',
      'lastTransition',
      'lastTransitionedAt',
      'transitions',
      'payinTotal',
      'payoutTotal',
      'lineItems',
      'protectedData',
      'metadata',
      'booking',
    ],
    'fields.booking': ['start', 'end', 'displayStart', 'displayEnd'],
    'fields.listing': [
      'title',
      'description',
      'availabilityPlan',
      'publicData.listingType',
      'publicData.transactionProcessAlias',
      'publicData.unitType',
      'publicData.featured',
      'publicData.featuredUntil',
    ],
    'fields.user': [
      'profile.displayName',
      'profile.abbreviatedName',
      'profile.publicData',
      'deleted',
      'banned',
    ],
    'fields.image': [
      'variants.square-small',
      'variants.square-small2x',
      'variants.square-xsmall',
      'variants.square-xsmall2x',
      'variants.listing-card',
      'variants.listing-card-2x',
      'variants.default',
      'variants.scaled-small',
      'variants.scaled-medium',
    ],
    page: 1,
    perPage: DASHBOARD_PAGE_SIZE,
    // Marketplace API: no leading minus = newest first on this host.
    sort: 'lastTransitionedAt',
  };
};

const emptyTxResponse = { data: { data: [], included: [] } };

const listFromQueryResponse = response => {
  const raw = response?.data?.data;
  if (Array.isArray(raw)) {
    return raw;
  }
  return raw ? [raw] : [];
};

const queryTransactions = (sdk, only) =>
  retryOn429(() => sdk.transactions.query(transactionQueryParams(only)), { retries: 1, jitter: 0 });

/**
 * Pin requested + accepted sales/orders the same way My Bookings does.
 * Recency page 1 is often past reviews/cancels and drops today’s upcoming.
 *
 * @param {Object} sdk
 * @param {'order'|'sale'} only
 * @param {Object} recencyResponse
 * @param {Function} dispatch
 * @returns {Promise<Object>}
 */
const pinUpcomingTransactions = async (sdk, only, recencyResponse, dispatch) => {
  if (!sdk?.transactions?.query || recencyResponse?.data?.data == null) {
    return recencyResponse;
  }
  try {
    const upcomingRes = await retryOn429(
      () =>
        sdk.transactions.query({
          ...transactionQueryParams(only),
          lastTransitions: UPCOMING_LAST_TRANSITIONS,
        }),
      { retries: 1, jitter: 0 }
    );
    const extra = listFromQueryResponse(upcomingRes);
    if (!extra.length) {
      return recencyResponse;
    }
    if (upcomingRes?.data?.data) {
      try {
        dispatch(addMarketplaceEntities(upcomingRes));
      } catch (err) {
        // Still merge the raw list so dashboard counts can see the booking.
      }
    }
    return {
      ...recencyResponse,
      data: {
        ...(recencyResponse.data || {}),
        included: [
          ...(recencyResponse.data?.included || []),
          ...(upcomingRes.data?.included || []),
        ],
        data: mergeInboxTransactions(extra, listFromQueryResponse(recencyResponse)),
      },
    };
  } catch (e) {
    return recencyResponse;
  }
};

const queryEarnedSales = sdk =>
  retryOn429(
    () =>
      sdk.transactions.query({
        only: 'sale',
        states: [
          'state/accepted',
          'state/delivered',
          'state/reviewed',
          'state/reviewed-by-customer',
          'state/reviewed-by-provider',
        ].join(','),
        include: ['listing', 'customer', 'booking'],
        'fields.transaction': [
          'createdAt',
          'processName',
          'processVersion',
          'lastTransition',
          'lastTransitionedAt',
          'transitions',
          'payinTotal',
          'payoutTotal',
          'lineItems',
          'protectedData',
          'metadata',
        ],
        'fields.listing': ['title'],
        'fields.user': [
      'profile.displayName',
      'profile.abbreviatedName',
      'profile.publicData',
      'deleted',
      'banned',
    ],
        page: 1,
        perPage: 100,
        sort: 'lastTransitionedAt',
      }),
    { retries: 1, jitter: 0 }
  );

const storeTransactions = (dispatch, response, previousRefs = []) => {
  if (response?.data?.data == null) {
    return previousRefs;
  }
  if (response.data.data.length) {
    dispatch(addMarketplaceEntities(response));
  }
  return entityRefs(response.data.data);
};

const loadDashboardPayload = async (_, { dispatch, getState, extra: sdk, rejectWithValue }) => {
  try {
    const [
      ordersResponse,
      salesResponse,
      earnedSalesResponse,
      ownListingsResponse,
    ] = await Promise.all([
      queryTransactions(sdk, 'order').catch(error =>
        isTooManyRequestsError(error) ? { data: { data: null } } : emptyTxResponse
      ),
      queryTransactions(sdk, 'sale').catch(error =>
        isTooManyRequestsError(error) ? { data: { data: null } } : emptyTxResponse
      ),
      queryEarnedSales(sdk).catch(error =>
        isTooManyRequestsError(error) ? { data: { data: null } } : emptyTxResponse
      ),
      queryOwnListeningProfiles(sdk).then(lookup => {
        if (lookup.error || lookup.listings == null) {
          return { data: { data: null }, ownListingsLookupError: lookup.error };
        }
        return { data: { data: lookup.listings } };
      }),
    ]);

    const [ordersPinned, salesPinned] = await Promise.all([
      pinUpcomingTransactions(sdk, 'order', ordersResponse, dispatch),
      pinUpcomingTransactions(sdk, 'sale', salesResponse, dispatch),
    ]);

    const currentUser = getState()?.user?.currentUser;
    const favoriteIds = favoriteListingIdsFromUser(currentUser);
    const favoriteProfileIds = favoriteProfileIdsFromUser(currentUser);
    const previousFavoriteRefs = getState()?.DashboardPage?.favoriteRefs || [];
    let favoriteRefs = previousFavoriteRefs;
    const favoriteQueryFields = {
      include: ['images', 'author', 'author.profileImage'],
      'fields.listing': ['title', 'description', 'publicData', 'state'],
      'fields.user': [
      'profile.displayName',
      'profile.abbreviatedName',
      'profile.publicData',
      'deleted',
      'banned',
    ],
      'fields.image': IMAGE_FIELD_VARIANTS,
      ...createImageVariantConfig('listing-card', 400, 1),
      ...createImageVariantConfig('listing-card-2x', 800, 1),
      ...createImageVariantConfig('square-small', 80, 1),
      ...createImageVariantConfig('square-small2x', 160, 1),
      ...createImageVariantConfig('square-xsmall', 40, 1),
      ...createImageVariantConfig('square-xsmall2x', 80, 1),
    };
    const queryFavoriteListings = params =>
      retryOn429(() => sdk.listings.query({ ...favoriteQueryFields, perPage: 40, ...params }), {
        retries: 1,
        jitter: 0,
      }).catch(error =>
        isTooManyRequestsError(error) ? { data: { data: null } } : emptyTxResponse
      );

    if (favoriteIds.length || favoriteProfileIds.length) {
      const favoriteResponse = favoriteIds.length
        ? await queryFavoriteListings({ ids: favoriteIds.join(',') })
        : emptyTxResponse;
      if (favoriteResponse?.data?.data == null && favoriteIds.length) {
        favoriteRefs = previousFavoriteRefs;
      } else {
        const listings = [...(favoriteResponse?.data?.data || [])];
        if (favoriteResponse?.data?.data?.length) {
          dispatch(addMarketplaceEntities(favoriteResponse));
        }
        const foundListingIds = new Set(listings.map(favoriteEntityId).filter(Boolean));
        const missingListingIds = favoriteIds.filter(id => !foundListingIds.has(id));
        const authorResponses = await Promise.all(
          missingListingIds.map(authorId => queryFavoriteListings({ authorId, perPage: 10 }))
        );
        authorResponses.forEach(response => {
          if (response?.data?.data == null) {
            return;
          }
          if (response.data.data.length) {
            dispatch(addMarketplaceEntities(response));
            listings.push(...response.data.data);
          }
        });

        const profileIdsToFetch = [
          ...favoriteProfileIds,
          ...missingListingIds.filter(
            id => !listings.some(listing => favoriteEntityId(listing?.author) === id)
          ),
        ];
        const users = [];
        const uniqueProfileIds = [...new Set(profileIdsToFetch)];
        const userResponses = await Promise.all(
          uniqueProfileIds.map(userId =>
            retryOn429(
              () =>
                sdk.users.show({
                  id: new UUID(userId),
                  include: ['profileImage'],
                  'fields.user': [
                    'profile.displayName',
                    'profile.abbreviatedName',
                    'deleted',
                    'banned',
                  ],
                  'fields.image': IMAGE_FIELD_VARIANTS,
                  ...createImageVariantConfig('square-small', 80, 1),
                  ...createImageVariantConfig('square-small2x', 160, 1),
                }),
              { retries: 1, jitter: 0 }
            ).catch(() => null)
          )
        );
        userResponses.forEach(response => {
          const user = response?.data?.data;
          if (!user) {
            return;
          }
          dispatch(addMarketplaceEntities(response));
          users.push(user);
        });

        const resolved = resolveFavoriteEntities({
          listingIds: favoriteIds,
          profileIds: favoriteProfileIds,
          listings,
          users,
        });
        favoriteRefs = resolved
          .map(entity => {
            const id = entity?.id;
            const type =
              entity?.type === 'user' || entity?.type === 'currentUser' ? 'user' : 'listing';
            return id ? { id, type } : null;
          })
          .filter(Boolean);

        const recovered = partitionRecoveredFavorites(favoriteIds, listings, users);
        const listingIdsChanged =
          recovered.listingIds.join(',') !== favoriteIds.join(',') ||
          recovered.profileIds.length > 0;
        if (listingIdsChanged && currentUser?.id?.uuid && sdk?.currentUser?.updateProfile) {
          const nextUser = applyFavoriteBucketsToUser(currentUser, recovered);
          dispatch(setCurrentUser(nextUser));
          const nextProfileIds = favoriteProfileIdsFromUser(nextUser);
          sdk.currentUser
            .updateProfile(
              {
                privateData: {
                  favoriteListingIds: recovered.listingIds,
                  favoriteProfileIds: nextProfileIds,
                },
              },
              { expand: true }
            )
            .catch(() => {});
        }
      }
    } else {
      favoriteRefs = [];
    }

    const previous = getState()?.DashboardPage || {};
    const previousOwnRefs = previous.ownListingRefs || [];
    const previousOrderRefs = previous.orderRefs || [];
    const previousSaleRefs = previous.saleRefs || [];
    const previousEarnedRefs = previous.earningsSaleRefs || [];
    const ownListingRows = ownListingsResponse?.data?.data;
    if (ownListingsResponse?.ownListingsLookupError || ownListingRows == null) {
      // Keep whatever listing we already have. A 429 is not “no profile”.
    } else if (ownListingRows?.length) {
      try {
        dispatch(addMarketplaceEntities(ownListingsResponse));
      } catch (err) {
        // Sparse own-listing payloads must still mark the profile as present.
      }
      dispatch(setCurrentUserHasListings(true));
      const ownListing = pickOwnListeningProfile(ownListingRows);
      const summary = summarizeOwnListeningProfile(ownListing);
      if (summary) {
        dispatch(setCurrentUserOwnListing(summary));
      }
      if (ownListing?.id && sdk?.ownListings?.show) {
        try {
          const shown = await retryOn429(() => sdk.ownListings.show({ id: ownListing.id }), {
            retries: 1,
            jitter: 0,
          });
          if (shown?.data?.data) {
            dispatch(addMarketplaceEntities(shown));
          }
        } catch (e) {
          // Keep the query row. Hours editor can still open empty.
        }
      }
      const stripeUser = getState()?.user?.currentUser;
      if (stripeUser?.stripeAccount && ownListing) {
        let stripeFetchKnown = false;
        try {
          await dispatch(fetchStripeAccount());
          stripeFetchKnown = true;
        } catch (e) {
          // 429 / network — never persist “not set up” from an empty fetch.
        }
        const stripeAccount = getState()?.stripeConnectAccount?.stripeAccount;
        const payoutsReady = stripeAccountReadyToPublish({
          currentUser: stripeUser,
          stripeAccount,
          payoutDetailsRequired: true,
        });
        await syncOwnListingPayoutsReady(sdk, ownListing, payoutsReady, {
          persistFalse: stripeFetchKnown && !!getStripeAccountData(stripeAccount),
        });
      }
    }

    dispatch(fetchCurrentUserNotifications()).catch(() => null);

    const salesWithFeedback = salesPinned?.data?.data
      ? {
          ...salesPinned,
          data: {
            ...salesPinned.data,
            data: await attachInboxListenerFeedback(salesPinned.data.data),
          },
        }
      : salesPinned;
    const saleRefs = storeTransactions(dispatch, salesWithFeedback, previousSaleRefs);
    const earnedRefs = storeTransactions(dispatch, earnedSalesResponse, previousEarnedRefs);
    return {
      orderRefs: storeTransactions(dispatch, ordersPinned, previousOrderRefs),
      saleRefs,
      earningsSaleRefs: earnedRefs.length ? earnedRefs : saleRefs,
      ownListingRefs: ownListingRows == null ? previousOwnRefs : entityRefs(ownListingRows),
      favoriteRefs,
    };
  } catch (e) {
    return rejectWithValue(storableError(e));
  }
};

export const loadDataThunk = createAsyncThunk('DashboardPage/loadData', loadDashboardPayload);

export const refreshDashboardThunk = createAsyncThunk(
  'DashboardPage/refreshData',
  loadDashboardPayload
);

const dashboardPageSlice = createSlice({
  name: 'DashboardPage',
  initialState: {
    fetchInProgress: false,
    fetchError: null,
    orderRefs: [],
    saleRefs: [],
    earningsSaleRefs: [],
    ownListingRefs: [],
    favoriteRefs: [],
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(loadDataThunk.pending, state => {
        state.fetchInProgress = true;
        state.fetchError = null;
      })
      .addCase(loadDataThunk.fulfilled, (state, action) => {
        state.fetchInProgress = false;
        state.orderRefs = action.payload.orderRefs;
        state.saleRefs = action.payload.saleRefs;
        state.earningsSaleRefs = action.payload.earningsSaleRefs || action.payload.saleRefs || [];
        state.ownListingRefs = action.payload.ownListingRefs || [];
        state.favoriteRefs = action.payload.favoriteRefs || [];
      })
      .addCase(loadDataThunk.rejected, (state, action) => {
        state.fetchInProgress = false;
        state.fetchError = action.payload;
      })
      .addCase(refreshDashboardThunk.fulfilled, (state, action) => {
        state.orderRefs = action.payload.orderRefs;
        state.saleRefs = action.payload.saleRefs;
        state.earningsSaleRefs = action.payload.earningsSaleRefs || action.payload.saleRefs || [];
        state.ownListingRefs = action.payload.ownListingRefs || [];
        state.favoriteRefs = action.payload.favoriteRefs || [];
      });
  },
});

export default dashboardPageSlice.reducer;

export const loadData = (params, search, config) => dispatch =>
  dispatch(loadDataThunk()).finally(() => dispatch(fetchFeaturedListingsThunk({ config })));

export const refreshDashboard = () => dispatch => dispatch(refreshDashboardThunk());
